import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AlloxApiError } from '../models';
import { AlloxApiStatus } from '../constants';
import { Subject, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { DialogService } from '@app/core/services/dialog.service';
import { AlloxApiErrorCode } from '@app/core';

@Injectable({ providedIn: 'root' })
export class ApiErrorHandlerService {
  private isShowConfirmDialog = false;

  // セッション切れ時のエラーメッセージ
  private static readonly SESSION_EXPIRED_MESSAGE = 'セッションが切れました。再ログインしてください';
  // メンテナンスモード時のエラーメッセージ
  private static readonly MAINTENANCE_MODE_MESSAGE = 'メンテナンス中のため画面操作できません。ログアウトします。';

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private authService: AuthService,
    private dialogService: DialogService
  ) {}

  /**
   * エラーハンドラー関数
   * @returns - `handle` メソッドをバインドした関数
   */
  get handler() {
    return this.handle.bind(this);
  }

  /**
   * APIレスポンスエラーハンドリング
   * @param error - 発生したエラー
   * @returns - エラーをスローするObservable
   */
  private handle(error: any) {
    let target = error;
    const size = 'modal-sm';

    if (Array.isArray(target)) {
      target = target.find(item => item instanceof AlloxApiError);
    }

    if (target instanceof AlloxApiError) {
      switch (target.status) {
        case AlloxApiStatus.BAD_REQUEST:
        case AlloxApiStatus.CONFLICT:
        case AlloxApiStatus.INTERNAL_SERVER_ERROR:
          this.showAlertDialog(error.message, size);
          break;

        case AlloxApiStatus.NOT_FOUND:
          // 404ページへリダイレクト
          this.router.navigate(['/', 'error', '404']);
          break;

        case AlloxApiStatus.INVALID_SESSION:
          switch (target.errorCode) {
            case AlloxApiErrorCode.AUTH_SESSION_EXPIRED:
              this.runSessionErrorAction(target.message);
              break;
            default:
              this.runSessionErrorAction();
              break;
          }
          return new Subject();

        case AlloxApiStatus.SERVICE_UNAVAILABLE:
          switch (target.errorCode) {
            case AlloxApiErrorCode.MAINTENANCE_MODE:
              this.maintenanceModeAction(target.message);
              break;
            default:
              break;
          }
          return new Subject();

        default:
          // その他のエラーはトーストで表示
          this.toastr.error(target.message);
          break;
      }
    } else if (target instanceof HttpErrorResponse) {
      // HTTPレスポンスエラーの処理
      this.toastr.error(target.message);
    } else {
      console.error(target);
      this.toastr.error(`${target}`);
    }
    return throwError(target);
  }

  /**
   * アラートダイアログを表示
   * @param message - 表示するメッセージ
   * @param size - モーダルサイズ
   */
  private showAlertDialog(message: string, size: string) {
    this.dialogService.openAlert(message, size);
  }

  /**
   * セッションエラー時の処理
   * @param message - 表示するエラーメッセージ（デフォルト: セッション切れメッセージ）
   */
  public runSessionErrorAction(message = ApiErrorHandlerService.SESSION_EXPIRED_MESSAGE) {
    this.showConfirmDialog(message, () => {
      this.authService.removeAlloxRole();
      window.location.replace(location.pathname);
    });
  }

  /**
   * メンテナンスモード時の処理
   * @param message - 表示するエラーメッセージ（デフォルト: メンテナンスモードメッセージ）
   */
  public maintenanceModeAction(message = ApiErrorHandlerService.MAINTENANCE_MODE_MESSAGE) {
    this.showConfirmDialog(message, () => {
      this.authService.removeAlloxRole();
      window.location.replace(location.pathname);
    });
  }

  /**
   * 確認ダイアログを表示
   * @param message - 表示するメッセージ
   * @param onCloseAction - ダイアログ閉じた後のアクション
   */
  private showConfirmDialog(message: string, onCloseAction: () => void) {
    if (!this.isShowConfirmDialog) {
      this.isShowConfirmDialog = true;
      const size = 'modal-sm';
      const modal = this.dialogService.openAlert(message, size);
      const content: any = modal.content;
      content.onClose.subscribe(_ => {
        onCloseAction();
        this.isShowConfirmDialog = false;
      });
    }
  }
}
